import { io, Socket } from 'socket.io-client';

type EventListener = (...args: any[]) => void;
type EventListenerMap = Map<string, EventListener[]>;

class SocketManager {
  private socket: Socket | null = null;
  private eventListeners: EventListenerMap = new Map();
  private rootUrl: string;
  private isConnected = false;

  constructor(rootUrl: string) {
    this.rootUrl = rootUrl;
  }

  /**
   * Initialize socket with token
   */
  initialize(token: string): Socket {
    if (this.socket?.connected) {
      this.disconnect();
    }

    this.socket = io(this.rootUrl, {
      reconnectionAttempts: 5,
      reconnectionDelay: 5,
      timeout: 10000,
      auth: {
        token,
      },
    });

    // Restore all saved event listeners
    this.restoreEventListeners();

    this.isConnected = true;
    return this.socket;
  }

  /**
   * Save event listener so it can be restored after reconnect
   */
  on(event: string, listener: EventListener): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.push(listener);
    }

    if (this.socket) {
      this.socket.on(event, listener);
    }
  }

  /**
   * Remove event listener
   */
  off(event: string, listener?: EventListener): void {
    if (listener) {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(listener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      this.eventListeners.delete(event);
    }

    if (this.socket) {
      if (listener) {
        this.socket.off(event, listener);
      } else {
        this.socket.off(event);
      }
    }
  }

  /**
   * Restore all saved event listeners
   * This method ensures all saved listeners are registered on the socket
   * Note: Socket.IO handles duplicate listeners gracefully, but we try to avoid them
   */
  private restoreEventListeners(): void {
    if (!this.socket) return;

    this.eventListeners.forEach((listeners, event) => {
      listeners.forEach((listener) => {
        if (this.socket) {
          // Remove listener first to avoid duplicates, then add it back
          this.socket.off(event, listener);
          this.socket.on(event, listener);
        }
      });
    });
  }

  /**
   * Update token and reconnect socket without losing event listeners
   */
  updateToken(newToken: string): void {
    if (!this.socket) {
      // If socket hasn't been initialized, initialize with new token
      this.initialize(newToken);
      return;
    }

    // Save current connection state
    const wasConnected = this.socket.connected;

    // Update auth token
    this.socket.auth = {
      token: newToken,
    };

    // If connected, disconnect and reconnect with new token
    if (wasConnected) {
      // Remove any existing 'connect' listener to avoid duplicates
      this.socket.off('connect');

      // Add a one-time listener to restore event listeners after reconnect
      const restoreOnConnect = () => {
        // Ensure all saved event listeners are registered
        // This is a safety measure in case any listeners were lost during reconnect
        this.restoreEventListeners();
        this.socket?.off('connect', restoreOnConnect);
      };

      this.socket.once('connect', restoreOnConnect);

      this.socket.disconnect();
      // Socket.IO will reconnect with new auth
      // Event listeners registered on socket will remain, but we restore them as a safety measure
      this.socket.connect();
    }
    // If not connected, just update auth
    // Event listeners are saved in eventListeners Map,
    // will be registered again when socket is reinitialized or connected
  }

  /**
   * Connect socket
   */
  connect(): void {
    if (this.socket) {
      this.socket.connect();
      this.isConnected = true;
    }
  }

  /**
   * Disconnect socket (but keep event listeners)
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  /**
   * Get socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  isSocketConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Remove all event listeners and disconnect
   */
  destroy(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.eventListeners.clear();
    this.isConnected = false;
  }
}

// Singleton instance
let socketManagerInstance: SocketManager | null = null;

/**
 * Get or create socket manager instance
 */
export function getSocketManager(rootUrl: string): SocketManager {
  if (!socketManagerInstance) {
    socketManagerInstance = new SocketManager(rootUrl);
  }
  return socketManagerInstance;
}

/**
 * Refresh socket with new token
 */
export function refreshSocketToken(newToken: string): void {
  const manager = socketManagerInstance;
  if (manager) {
    manager.updateToken(newToken);

    // Notify app layers (e.g. React components) that socket token has been refreshed
    // so they can re-bind any listeners if needed.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("socketTokenRefreshed"));
    }
  }
}

